import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, User, Building2, LogOut, Settings, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';
import { RoleSelector } from '@/components/RoleSelector';

export function TopNavigation({ onMenuToggle, isMobile, sidebarOpen }) {
  const { user, outlets, currentOutlet, switchOutlet, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadNotifications] = React.useState(3);

  // Map outlets to include address and city fields
  const displayOutlets = React.useMemo(() => {
    if (outlets.length > 0) {
      return outlets.map(o => ({
        ...o,
        address: o.address_street || o.address || '',
        city: o.address_city || o.city || '',
        country: o.address_country || o.country || 'NO',
      }));
    }
    // Fallback outlets if none loaded from API
    return [
      // Norway (Primary Market)
      { id: '1', name: 'Sørengkaia', country: 'NO', address: 'Sørengkaia 7', city: 'Oslo' },
      { id: '2', name: 'Frogner', country: 'NO', address: 'Frognerveien 2', city: 'Oslo' },
      // Sweden
      { id: '3', name: 'Hammarby Sjöstad', country: 'SE', address: 'Jan Inghes Torg 22', city: 'Stockholm' },
      { id: '4', name: 'Karlbergsvägen', country: 'SE', address: 'Karlsbergsvägen 43', city: 'Stockholm' },
      { id: '5', name: 'St Eriksgatan', country: 'SE', address: 'St Eriksgatan 116', city: 'Stockholm' },
      { id: '6', name: 'Götgatan (Ringen)', country: 'SE', address: 'Götgatan 100', city: 'Stockholm' },
      { id: '7', name: 'Drottninggatan', country: 'SE', address: 'Drottninggatan 73 b', city: 'Stockholm' },
      { id: '8', name: 'Solna', country: 'SE', address: 'Trubadurvägen 12', city: 'Solna' },
    ];
  }, [outlets]);

  const selectedOutlet = currentOutlet?.name || displayOutlets[0]?.name || 'Select Outlet';
  const userInitials = user?.full_name ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U';
  const userName = user?.full_name || user?.role || 'User';
  const userEmail = user?.email || '';

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <nav 
      data-testid="top-navigation"
      className="bg-brand-charcoal text-white border-b border-gray-800 sticky top-0 z-50"
    >
      <div className="flex items-center justify-between h-16 px-3 md:px-6">
        {/* Left Section - Menu Toggle, Logo & Brand */}
        <div className="flex items-center gap-2 md:gap-6">
          {/* Mobile Menu Toggle */}
          {isMobile && (
            <Button
              data-testid="mobile-menu-toggle"
              variant="ghost"
              size="icon"
              onClick={onMenuToggle}
              className="text-white hover:bg-white/10 lg:hidden"
              aria-label="Toggle menu"
            >
              <Menu className="h-6 w-6" />
            </Button>
          )}

          {/* Logo */}
          <div data-testid="brand-logo" className="flex items-center">
            <img 
              src="/logo-isf.png"
              alt="Indian Street Food & Co"
              className="h-8 md:h-10 w-auto"
            />
          </div>

          {/* Outlet Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="outlet-selector"
                variant="ghost"
                className="text-white hover:bg-white/10 gap-1 md:gap-2 px-2 md:px-4"
              >
                <Building2 className="h-4 w-4" />
                <span className="hidden sm:inline max-w-[120px] md:max-w-none truncate">{selectedOutlet}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuLabel>Norway</DropdownMenuLabel>
              {displayOutlets.filter(o => o.country === 'NO').map((outlet) => (
                <DropdownMenuItem
                  key={outlet.id || outlet.name}
                  data-testid={`outlet-${outlet.name.toLowerCase().replace(/\s+/g, '-').replace(/,/g, '')}`}
                  onClick={() => switchOutlet(outlet)}
                  className="gap-2"
                >
                  <span className={`w-2 h-2 rounded-full ${outlet.name === selectedOutlet ? 'bg-spice-cardamom' : 'bg-gray-300'}`} />
                  <div className="flex flex-col">
                    <span>{outlet.name}</span>
                    <span className="text-xs text-muted-foreground">{outlet.address}</span>
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Sweden</DropdownMenuLabel>
              {displayOutlets.filter(o => o.country === 'SE').map((outlet) => (
                <DropdownMenuItem
                  key={outlet.id || outlet.name}
                  data-testid={`outlet-${outlet.name.toLowerCase().replace(/\s+/g, '-').replace(/,/g, '')}`}
                  onClick={() => switchOutlet(outlet)}
                  className="gap-2"
                >
                  <span className={`w-2 h-2 rounded-full ${outlet.name === selectedOutlet ? 'bg-spice-cardamom' : 'bg-gray-300'}`} />
                  <div className="flex flex-col">
                    <span>{outlet.name}</span>
                    <span className="text-xs text-muted-foreground">{outlet.address}, {outlet.city}</span>
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-muted-foreground gap-2">
                <span className="w-2 h-2" />
                All Outlets
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Right Section - Role Selector, Notifications & User */}
        <div className="flex items-center gap-2 md:gap-4">
          {/* Role Selector (Admin only) - Hidden on small mobile */}
          <div className="hidden sm:block">
            <RoleSelector />
          </div>

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="notification-bell"
                variant="ghost"
                size="icon"
                className="relative text-white hover:bg-white/10"
              >
                <Bell className="h-5 w-5" />
                {unreadNotifications > 0 && (
                  <span 
                    data-testid="notification-badge"
                    className="absolute top-1 right-1 h-4 w-4 bg-brand-orange rounded-full text-[10px] font-semibold flex items-center justify-center border-2 border-brand-charcoal"
                  >
                    {unreadNotifications}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="px-4 py-3 border-b">
                <p className="font-semibold">Notifications</p>
              </div>
              <div className="max-h-64 overflow-y-auto">
                <DropdownMenuItem className="flex flex-col items-start gap-1 py-3 cursor-pointer">
                  <span className="font-medium text-sm">Low Stock Alert</span>
                  <span className="text-xs text-muted-foreground">Chicken tikka masala below par level</span>
                  <span className="text-xs text-brand-orange">2 min ago</span>
                </DropdownMenuItem>
                <DropdownMenuItem className="flex flex-col items-start gap-1 py-3 cursor-pointer">
                  <span className="font-medium text-sm">Approval Required</span>
                  <span className="text-xs text-muted-foreground">Expense claim kr 3 200 pending</span>
                  <span className="text-xs text-brand-orange">15 min ago</span>
                </DropdownMenuItem>
                <DropdownMenuItem className="flex flex-col items-start gap-1 py-3 cursor-pointer">
                  <span className="font-medium text-sm">Shift Reminder</span>
                  <span className="text-xs text-muted-foreground">Evening shift starts in 30 min</span>
                  <span className="text-xs text-brand-orange">30 min ago</span>
                </DropdownMenuItem>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="justify-center text-brand-orange font-medium cursor-pointer">
                View All Notifications
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="user-menu"
                variant="ghost"
                className="text-white hover:bg-white/10 gap-1 md:gap-2 px-2 md:px-4"
              >
                <div className="w-8 h-8 bg-brand-orange rounded-full flex items-center justify-center text-sm font-semibold">
                  {userInitials}
                </div>
                <span className="hidden md:inline">{userName}</span>
                <ChevronDown className="h-4 w-4 hidden sm:block" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2 border-b">
                <p className="font-semibold">{userName}</p>
                <p className="text-sm text-muted-foreground truncate">{userEmail}</p>
              </div>
              {/* Role Selector for mobile */}
              <div className="sm:hidden px-2 py-2 border-b">
                <RoleSelector />
              </div>
              <DropdownMenuItem data-testid="user-profile" className="gap-2 cursor-pointer">
                <User className="h-4 w-4" />
                My Profile
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="user-settings" className="gap-2 cursor-pointer">
                <Settings className="h-4 w-4" />
                Preferences
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                data-testid="user-logout" 
                className="gap-2 text-destructive cursor-pointer"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  );
}
